import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Hook for fetching service categories
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  return { categories, isLoading, error };
};

// Hook for fetching services
export const useServices = (categoryId) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const url = categoryId 
          ? `${API}/services?category_id=${categoryId}`
          : `${API}/services`;
        const response = await axios.get(url);
        setServices(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, [categoryId]);

  return { services, isLoading, error };
};

// Hook for fetching bookings
export const useBookings = (status) => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = status 
        ? `${API}/bookings?status=${status}`
        : `${API}/bookings`;
      const response = await axios.get(url);
      setBookings(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [status]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { bookings, isLoading, error, refetch };
};

// Hook for fetching wallet
export const useWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const [walletRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/transactions`)
      ]);
      setWallet(walletRes.data);
      const txData = transactionsRes.data;
      setTransactions(Array.isArray(txData) ? txData : (txData.transactions || []));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { wallet, transactions, isLoading, error, refetch };
};

// Hook for fetching addresses
export const useAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/addresses`);
      setAddresses(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const addAddress = async (addressData) => {
    const response = await axios.post(`${API}/addresses`, addressData);
    setAddresses(prev => [...prev, response.data]);
    return response.data;
  };

  const deleteAddress = async (addressId) => {
    await axios.delete(`${API}/addresses/${addressId}`);
    setAddresses(prev => prev.filter(a => a.id !== addressId));
  };

  return { addresses, isLoading, error, refetch, addAddress, deleteAddress };
};

// Hook for provider profile
export const useProviderProfile = (providerId) => {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!providerId) return;
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/providers/${providerId}`);
      setProfile(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { profile, isLoading, error, refetch };
};

// Hook for admin dashboard
export const useAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
};

// API helper functions
export const api = {
  // Bookings
  createBooking: (data) => axios.post(`${API}/bookings`, data),
  getBooking: (id) => axios.get(`${API}/bookings/${id}`),
  updateBookingStatus: (id, data) => axios.put(`${API}/bookings/${id}/status`, data),
  
  // Wallet
  getWallet: () => axios.get(`${API}/wallet`),
  topUpWallet: (data) => axios.post(`${API}/wallet/topup`, data),
  requestPayout: (data) => axios.post(`${API}/wallet/payout`, data),
  
  // Generic methods
  get: (url) => axios.get(`${API}${url}`),
  
  // Reviews
  createReview: (data) => axios.post(`${API}/reviews`, data),
  getProviderReviews: (providerId) => axios.get(`${API}/providers/${providerId}/reviews`),
  
  // Provider
  toggleOnline: () => axios.post(`${API}/provider/toggle-online`),
  updateProfile: (data) => axios.put(`${API}/provider/profile`, data),
  uploadDocument: (formData, params) => axios.post(`${API}/provider/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params
  }),
  getDocuments: () => axios.get(`${API}/provider/documents`),
  
  // Admin
  getPendingProviders: () => axios.get(`${API}/admin/providers/pending`),
  verifyProvider: (providerId, status) => axios.put(`${API}/admin/providers/${providerId}/verify?status=${status}`),
  getAdminBookings: (params) => axios.get(`${API}/admin/bookings`, { params }),
  getSettings: () => axios.get(`${API}/admin/settings`),
  updateCommission: (data) => axios.put(`${API}/admin/settings/commission`, data),
  updateSMTP: (data) => axios.put(`${API}/admin/settings/smtp`, data),
  updateResend: (data) => axios.put(`${API}/admin/settings/resend`, data),
  updateSMS: (data) => axios.put(`${API}/admin/settings/sms`, data),
  updatePayMongo: (data) => axios.put(`${API}/admin/settings/paymongo`, data),
  updateStorage: (data) => axios.put(`${API}/admin/settings/storage`, data),
  updateMaps: (data) => axios.put(`${API}/admin/settings/maps`, data),
  updateFirebase: (data) => axios.put(`${API}/admin/settings/firebase`, data),
  getFirebaseConfig: () => axios.get(`${API}/admin/settings/firebase/config`),
  registerPushToken: (token) => axios.post(`${API}/notifications/register-push-token?token=${token}`),
  createShareLink: (bookingId) => axios.post(`${API}/bookings/${bookingId}/share`),
  getSharedBooking: (shareToken) => axios.get(`${API}/public/booking/${shareToken}`),
  getAdminBookingDetails: (bookingId) => axios.get(`${API}/admin/bookings/${bookingId}/full-details`),
  testSMS: (phone) => axios.post(`${API}/admin/settings/test-sms?phone=${phone}`),
  testEmail: (email) => axios.post(`${API}/admin/settings/test-email?email=${email}`),
  seedData: () => axios.post(`${API}/admin/seed`),
  
  // Categories
  createCategory: (data) => axios.post(`${API}/admin/categories`, data),
  updateCategory: (id, data) => axios.put(`${API}/admin/categories/${id}`, data),
  
  // Services
  createService: (data) => axios.post(`${API}/admin/services`, data),
  
  // Chat
  sendChatMessage: (data) => axios.post(`${API}/chat/messages`, data),
  getChatMessages: (bookingId) => axios.get(`${API}/chat/messages/${bookingId}`),
  getUnreadCount: () => axios.get(`${API}/chat/unread-count`),
  
  // Notifications
  getNotifications: (params) => axios.get(`${API}/notifications`, { params }),
  markNotificationRead: (id) => axios.put(`${API}/notifications/${id}/read`),
  markAllNotificationsRead: () => axios.put(`${API}/notifications/read-all`),
  
  // Booking Details
  getBookingDetails: (id) => axios.get(`${API}/bookings/${id}/details`),
  updateProviderLocation: (id, lat, lng) => axios.put(`${API}/bookings/${id}/provider-location?latitude=${lat}&longitude=${lng}`),
  getProviderLocation: (id) => axios.get(`${API}/bookings/${id}/provider-location`),
  
  // Disputes
  createDispute: (data) => axios.post(`${API}/disputes`, data),
  getDisputes: () => axios.get(`${API}/disputes`),
  getDispute: (id) => axios.get(`${API}/disputes/${id}`),
  updateDispute: (id, data) => axios.put(`${API}/admin/disputes/${id}`, data),
  getAdminDisputes: (params) => axios.get(`${API}/admin/disputes`, { params }),
  
  // Promo Codes
  createPromoCode: (data) => axios.post(`${API}/admin/promo-codes`, data),
  getPromoCodes: () => axios.get(`${API}/admin/promo-codes`),
  updatePromoCode: (id, data) => axios.put(`${API}/admin/promo-codes/${id}`, data),
  deletePromoCode: (id) => axios.delete(`${API}/admin/promo-codes/${id}`),
  applyPromoCode: (data) => axios.post(`${API}/promo-codes/apply`, data),
  recordPromoUsage: (code, bookingId) => axios.post(`${API}/promo-codes/record-usage?code=${code}&booking_id=${bookingId}`),
  
  // Invoice/Receipt
  downloadInvoice: (bookingId) => axios.get(`${API}/bookings/${bookingId}/invoice`, { responseType: 'blob' }),
  downloadReceipt: (bookingId) => axios.get(`${API}/bookings/${bookingId}/receipt`, { responseType: 'blob' }),
};
